import { LevelPhase, SimConstants, dailyScore, starCount } from "@highmarch/domain";
import { dailyDayLabel } from "./modes/dailyDate";
import type { LevelSim, StarMask } from "@highmarch/domain";
import type { Moment } from "../level/moments";
import type { TowerDamageRow } from "./damageReport";
import { PLATE_H, TitlePlate, drawTitlePlate, wornTitlePlate } from "./titleFrameCanvas";

/**
 * The shareable end card (ENGAGEMENT B8, QoL U19): the player's own road and towers drawn as a small map, the stars,
 * and the run's top moments, handed to the system share sheet (`navigator.share`, as a PNG where files can be
 * shared, else the text), or copied when there is no share sheet. Nothing leaves the device unless the player sends it.
 */
export interface ShareCardModel {
  readonly title: string;
  readonly won: boolean;
  readonly stars: number;
  readonly moments: readonly string[];
  readonly mvp: string;
  readonly width: number;
  readonly height: number;
  readonly road: readonly { x: number; y: number }[];
  readonly towers: readonly { x: number; y: number; id: string; tier: number }[];
  readonly base: { x: number; y: number };
  readonly portal: { x: number; y: number };
  /** A Daily Road run's score (MP-20): drawn in place of the stars, said in the text. */
  readonly score?: number;
  /** MP-28: a challenge code's run: its clear time (or the wave it reached), drawn in place of the stars, said in the text. */
  readonly clear?: string;
  /** MP-27: the Hall of Records' lines about this run ("New record · 3:07"), drawn under the moments, said in the text. */
  readonly records?: readonly string[];
}

/** MP-27: where the end card's record lines come from (features/records.ts sets it; none until then). */
let recordLines: (sim: LevelSim) => readonly string[] = () => [];
export function setShareRecordLines(source: (sim: LevelSim) => readonly string[]): void { recordLines = source; }

/** What the card says about a run: built from the sim as the end screen shows it, the run's stars and moments. */
export function shareCardModel(sim: LevelSim, run: StarMask, moments: readonly Moment[], top: readonly TowerDamageRow[]): ShareCardModel {
  const won = sim.phase === LevelPhase.Won;
  const mvp = top[0] ? `${top[0].name} dealt ${Math.round(top[0].share * 100)} % of the damage` : "";
  // MP-20: a Daily Road card names its day, the same road every player had, and carries the score
  const daily = sim.mods.daily, score = daily ? dailyScore(sim.balance.modes.daily, { won, stagesCleared: sim.stagesCleared, baseHp: won ? sim.baseHp : 0 }) : undefined;
  // MP-28: a challenge code's card names the code, so whoever sees it can play the same battle
  const code = sim.mods.challenge?.code, records = recordLines(sim);
  const clear = code ? (won ? `TIME ${clearTime(sim.tick)}` : `WAVE ${sim.stagesCleared}/${sim.stages}`) : undefined;
  return {
    title: daily ? `Daily Road · ${dailyDayLabel(daily.day)}` : code ? `Challenge ${code}` : `Level ${sim.levelNumber} · ${sim.level.name}`,
    won, stars: won && !daily && !code ? starCount(run) : 0, ...(score === undefined ? {} : { score }), ...(clear === undefined ? {} : { clear }), ...(records.length > 0 ? { records } : {}),
    moments: moments.map((m) => (m.sub ? `${m.title} · ${m.sub}` : m.title)),
    mvp,
    width: sim.grid.width, height: sim.grid.height,
    road: sim.road.roadCells.map((c) => ({ x: c.x, y: c.y })),
    towers: sim.towerSlots.filter((t) => t.alive && !sim.balance.towers[t.archetype].isBase).map((t) => ({ x: t.cell.x, y: t.cell.y, id: sim.balance.towers[t.archetype].id, tier: t.tier })),
    base: { x: sim.grid.base.x, y: sim.grid.base.y },
    portal: { x: sim.road.portal.x, y: sim.road.portal.y },
  };
}

/** Sim ticks as "3:07" (20 ticks a second). */
function clearTime(ticks: number): string {
  const s = Math.round(ticks / SimConstants.TicksPerSecond);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** The game's name on the card and in the shared text. */
export const GAME_NAME = "Highmarch";

/** The text shared with (or instead of) the picture: one line a chat app shows in full. */
export function shareText(m: ShareCardModel): string {
  const stars = m.score !== undefined ? ` · score ${m.score}` : m.clear !== undefined ? ` · ${m.clear.toLowerCase()}` : m.won ? ` ${"★".repeat(m.stars)}${"☆".repeat(3 - m.stars)}` : "";
  const bits = [`${m.title} — ${m.won ? "VICTORY" : "DEFEAT"}${stars}`, ...(m.records ?? []), ...m.moments.slice(0, 2), m.mvp].filter((s) => s !== "");
  return `${bits.join(" · ")} (${GAME_NAME})`;
}

/** Map colours per tower (the card's own small palette, near each tower's element). */
const TOWER_COLOUR: Readonly<Record<string, string>> = {
  archer: "#7fc66a", cannon: "#9aa3b5", frost: "#7fd8ff", fire: "#ff7a3c", lightning: "#ffe066", ballista: "#c8955a", poison: "#8fe05a", beacon: "#e0a8ff",
  mortar: "#d0884a", barracks: "#c94a3c", arcane: "#ff4fc8", hex: "#b45cff",
};
/** The card's size (px): a phone-friendly portrait image. */
export const CARD_W = 540;
export const CARD_H = 720;
/** The map's box on the card. */
const MAP = { x: 30, y: 150, w: 480, h: 420 } as const;

/** Draws the card onto a 2D context of CARD_W × CARD_H; `plate`: the worn title frame's name plate (MP-23), at the foot. */
export function drawShareCard(g: CanvasRenderingContext2D, m: ShareCardModel, plate: TitlePlate | null = null): void {
  const bg = g.createLinearGradient(0, 0, 0, CARD_H);
  bg.addColorStop(0, "#1d2740"); bg.addColorStop(1, "#0f1524");
  g.fillStyle = bg; g.fillRect(0, 0, CARD_W, CARD_H);
  g.textAlign = "center"; g.fillStyle = "#ffd75c";
  g.font = "bold 40px 'Fredoka Display', sans-serif";
  g.fillText(m.won ? "VICTORY" : "DEFEAT", CARD_W / 2, 62);
  g.fillStyle = "#fff8e6"; g.font = "bold 22px 'Fredoka Display', sans-serif";
  g.fillText(m.title, CARD_W / 2, 98);
  if (m.score !== undefined) { g.fillStyle = "#ffd75c"; g.font = "bold 30px 'Fredoka Display', sans-serif"; g.fillText(`SCORE ${m.score}`, CARD_W / 2, 136); }
  else if (m.clear !== undefined) { g.fillStyle = "#ffd75c"; g.font = "bold 30px 'Fredoka Display', sans-serif"; g.fillText(m.clear, CARD_W / 2, 136); }
  else if (m.won) { g.fillStyle = "#ffd75c"; g.font = "34px 'Fredoka Display', sans-serif"; g.fillText("★".repeat(m.stars) + "☆".repeat(3 - m.stars), CARD_W / 2, 136); }
  // the map: every cell faint, the road in sand, the towers as dots in their colour, the fortress and the portal
  const cell = Math.min(MAP.w / m.width, MAP.h / m.height);
  const ox = MAP.x + (MAP.w - cell * m.width) / 2, oy = MAP.y + (MAP.h - cell * m.height) / 2;
  const at = (c: { x: number; y: number }) => ({ x: ox + c.x * cell, y: oy + (m.height - 1 - c.y) * cell });
  g.fillStyle = "rgba(120, 160, 90, 0.35)"; g.fillRect(ox, oy, cell * m.width, cell * m.height);
  g.fillStyle = "#d9bb85";
  for (const c of m.road) { const p = at(c); g.fillRect(p.x + 1, p.y + 1, cell - 2, cell - 2); }
  for (const t of m.towers) {
    const p = at(t);
    g.fillStyle = TOWER_COLOUR[t.id] ?? "#ffffff";
    g.beginPath(); g.arc(p.x + cell / 2, p.y + cell / 2, cell * (0.28 + 0.06 * t.tier), 0, Math.PI * 2); g.fill();
  }
  const b = at(m.base), pt = at(m.portal);
  g.fillStyle = "#ffd75c"; g.fillRect(b.x, b.y, cell, cell);
  g.fillStyle = "#b27cff"; g.beginPath(); g.arc(pt.x + cell / 2, pt.y + cell / 2, cell * 0.45, 0, Math.PI * 2); g.fill();
  g.fillStyle = "#fff8e6"; g.font = "bold 20px 'Fredoka Display', sans-serif";
  let y = MAP.y + MAP.h + (plate ? 28 : 40);
  // three lines fit under the map: a record first (in gold), then the moments and the MVP
  const record = (m.records ?? []).slice(0, 1);
  const lines = [...record, ...[...m.moments.slice(0, 2), m.mvp].filter((s) => s !== "")].slice(0, 3);
  for (const [i, line] of lines.entries()) { g.fillStyle = i < record.length ? "#ffd75c" : "#fff8e6"; g.fillText(line, CARD_W / 2, y); y += plate ? 25 : 30; }
  // MP-23: the worn title frame's plate at the foot, the game's name tucked under it
  if (plate) drawTitlePlate(g, plate, CARD_W / 2, CARD_H - PLATE_H - 22);
  g.fillStyle = "#b98a37"; g.font = plate ? "13px 'Fredoka Display', sans-serif" : "16px sans-serif";
  g.fillText(GAME_NAME, CARD_W / 2, plate ? CARD_H - 3 : CARD_H - 18);
}

/** The platform's share sheet and clipboard, as far as the card needs them (a fake in tests). */
export interface ShareHost {
  share?: (data: ShareData) => Promise<void>;
  canShare?: (data: ShareData) => boolean;
  clipboard?: { writeText(text: string): Promise<void> };
}

export type ShareOutcome = "shared" | "copied" | "cancelled" | "failed";

/**
 * Shares the card: the picture with the text where the share sheet takes files, the text alone where it does not,
 * or the text copied to the clipboard with no share sheet at all. A dismissed sheet is "cancelled", not an error.
 */
export async function shareCard(host: ShareHost, text: string, png: Blob | null): Promise<ShareOutcome> {
  const title = GAME_NAME;
  try {
    if (host.share) {
      const file = png && typeof File !== "undefined" ? new File([png], "highmarch.png", { type: "image/png" }) : null;
      const withFile: ShareData = file ? { title, text, files: [file] } : { title, text };
      await host.share(file && host.canShare?.(withFile) ? withFile : { title, text });
      return "shared";
    }
    if (host.clipboard) { await host.clipboard.writeText(text); return "copied"; }
    return "failed";
  } catch (e) {
    return e instanceof Error && e.name === "AbortError" ? "cancelled" : "failed";
  }
}

/** The card as a PNG (null where a canvas cannot be made or encoded). */
export async function renderShareCard(m: ShareCardModel): Promise<Blob | null> {
  if (typeof document === "undefined") return null;
  const canvas = document.createElement("canvas");
  canvas.width = CARD_W; canvas.height = CARD_H;
  const g = canvas.getContext("2d");
  if (!g) return null;
  drawShareCard(g, m, await wornTitlePlate());
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png"));
}

/** Whether this platform can share or copy at all (the end screen shows SHARE only then). */
export function canShareCard(host: ShareHost): boolean { return typeof host.share === "function" || typeof host.clipboard?.writeText === "function"; }
